import { useEffect, useState } from 'react'
import {
  autoUpdate,
  useFloating,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from '@floating-ui/react'

import { TextButton } from '../buttons/textButton'
import './tab.css'

interface TabProps {
  label: string
  onClick: () => void
  selected?: boolean
  thumbnail?: string
}

export const Tab = ({ label, onClick, selected, thumbnail }: TabProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const { refs, floatingStyles, context } = useFloating<HTMLButtonElement>({
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift(),
    ],
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
  })

  // Event listeners to change the open state
  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ])

  // Scroll the tab into view when selected.
  useEffect(() => {
    if (selected && refs.reference.current) {
      const elem = refs.reference.current as HTMLButtonElement
      elem.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      })
    }
  }, [label, refs, selected])

  return (
    <div style={{ position: 'relative' }}>
      <TextButton
        className={selected ? 'tab-bar-tab-selected' : undefined}
        label={label}
        onClick={onClick}
        ref={refs.setReference}
        {...getReferenceProps()}
      />
      <FloatingPortal>
        {thumbnail && isOpen && (
          <div
            className="tab-thumbnail-container"
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            <img src={thumbnail} alt={label} />
          </div>
        )}
      </FloatingPortal>
    </div>
  )
}
